import { clsx } from 'clsx'
import { type ComponentPropsWithoutRef, useMemo, type VFC } from 'react'

import { type Variant } from 'views/components/organisms/FilteredProductList/CompactProductListItem'
import CompactProductListItemVariant from 'views/components/organisms/FilteredProductList/CompactProductListItem/CompactProductListItemVariant'

import './CompactProductListItemVariantList.scss'

type CompactProductListItemVariantListProps = ComponentPropsWithoutRef<'ul'> & {
  selectedVariantId: Variant['id']
  variants: Variant[]
}

const MAX_VISIBLE_VARIANTS = 2

const CompactProductListItemVariantList: VFC<
  CompactProductListItemVariantListProps
> = props => {
  const { className, selectedVariantId, variants, ...rest } = props

  /**
   * Selected variant should always be visible in variants list. If originally it
   * is out of visible range we put it last visible element.
   */
  const visibleVariants = useMemo(() => {
    const items = variants.slice(0, MAX_VISIBLE_VARIANTS)

    const selectedVariantIndex = variants.findIndex(
      variant => variant.id === selectedVariantId
    )

    if (selectedVariantIndex >= MAX_VISIBLE_VARIANTS) {
      items[MAX_VISIBLE_VARIANTS - 1] = variants[selectedVariantIndex]
    }

    return items
  }, [selectedVariantId, variants])

  return (
    <ul
      className={clsx('o-CompactProductListItemVariantList', className)}
      {...rest}
    >
      {visibleVariants.map(variant => (
        <li key={variant.id}>
          <CompactProductListItemVariant
            selected={variant.id === selectedVariantId}
            variant={variant}
          />
        </li>
      ))}
      {variants.length > MAX_VISIBLE_VARIANTS && (
        <li className="o-CompactProductListItemVariantList__counter">
          +{variants.length - MAX_VISIBLE_VARIANTS}
        </li>
      )}
    </ul>
  )
}

export default CompactProductListItemVariantList
